/**
 * useFileUpload — Shared upload logic for ImageUpload, VideoUpload, AudioUpload nodes.
 *
 * Handles:
 * - File validation (type + per-type size limits: image 10MB, video 100MB, audio 50MB)
 * - Object URL lifecycle (create, revoke on replace, revoke on unmount)
 * - Upload via backend API (POST /projects/{id}/assets/upload)
 * - Status transitions (idle -> running -> success/error)
 * - Drag-and-drop support
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@ponpon/api-client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import type { NodeDataRecord } from '../../nodeRegistry'

/** Per-media-type file size limits in bytes */
export const MAX_FILE_SIZE: Record<string, number> = {
  image: 10 * 1024 * 1024,   // 10 MB
  video: 100 * 1024 * 1024,  // 100 MB
  audio: 50 * 1024 * 1024,   // 50 MB
}

/**
 * Returns an error message if the file exceeds the per-type size limit,
 * or null if the file is within the limit.
 */
export function getFileSizeError(fileSize: number, mediaType: string): string | null {
  const maxSize = MAX_FILE_SIZE[mediaType] ?? 10 * 1024 * 1024
  if (fileSize > maxSize) {
    const maxSizeMB = maxSize / (1024 * 1024)
    return `${mediaType.charAt(0).toUpperCase() + mediaType.slice(1)} files must be under ${maxSizeMB} MB`
  }
  return null
}

/** Map media type to the schema output field name (out_image, out_video, out_audio) */
const MEDIA_TO_OUT_FIELD: Record<string, string> = {
  image: 'out_image',
  video: 'out_video',
  audio: 'out_audio',
}

interface UseFileUploadOptions {
  acceptMime: string // e.g., 'image', 'video', 'audio'
  updateNodeData: (updates: NodeDataRecord) => void
  /** The persisted out_* value from YDoc (e.g. data.out_image).
   *  When this becomes truthy, the blob preview URL is revoked. */
  persistedValue?: string
  /** Eagerly-created blob URL for instant preview (e.g. from Director upload).
   *  Seeds previewUrl and isUploading state so the first render shows the
   *  uploading state with a preview instead of the empty upload area. */
  initialPreviewUrl?: string
}

interface UseFileUploadReturn {
  /** Current preview URL (object URL or resolved asset URL) */
  previewUrl: string | null
  /** Whether an upload is currently in progress */
  isUploading: boolean
  /** Current error message, if any */
  error: string | null
  /** Handle file input change */
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Drag-and-drop event handlers */
  dragHandlers: {
    onDragOver: (e: React.DragEvent) => void
    onDragLeave: (e: React.DragEvent) => void
    onDrop: (e: React.DragEvent) => void
  }
  /** Whether the drag zone is currently highlighted */
  isDragOver: boolean
  /** Retry the last failed upload. Only meaningful when error is set. */
  retry: () => void
  /** Clear the upload state (for Remove button) */
  clear: () => void
  /** Temporary warning shown when multiple files are dropped */
  multiFileWarning: string | null
  /** Programmatically upload a file (for Director upload and canvas-level drop) */
  uploadFile: (file: File) => void
}

export function useFileUpload({
  acceptMime,
  updateNodeData,
  persistedValue,
  initialPreviewUrl,
}: UseFileUploadOptions): UseFileUploadReturn {
  const projectId = useProjectId()
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialPreviewUrl ?? null)
  const [isUploading, setIsUploading] = useState(!!initialPreviewUrl)
  const [error, setError] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [multiFileWarning, setMultiFileWarning] = useState<string | null>(null)
  const objectUrlRef = useRef<string | null>(initialPreviewUrl ?? null)
  const lastFileRef = useRef<File | null>(null)

  // Once YDoc confirms the persisted value, revoke the blob preview URL.
  // This avoids the flash of empty state that would occur if we cleared
  // previewUrl eagerly in processFile (YDoc propagation is async).
  useEffect(() => {
    if (persistedValue && objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
      setPreviewUrl(null)
    }
  }, [persistedValue])

  // Cleanup refs on unmount.
  // We intentionally do NOT revoke objectUrlRef here — React 18 StrictMode
  // simulates unmount/remount, which would revoke the blob URL while an
  // upload is still in progress (breaking the spinner preview). Blob URLs
  // are explicitly revoked on success, error, clear, and replacement;
  // any remaining ones are freed automatically on page unload.
  useEffect(() => {
    return () => {
      objectUrlRef.current = null
      lastFileRef.current = null
    }
  }, [])

  const processFile = useCallback(
    async (file: File) => {
      // Validate MIME type
      if (!file.type.startsWith(`${acceptMime}/`)) {
        setError(`Invalid file type. Expected ${acceptMime}/*.`)
        updateNodeData({ status: 'error', error: `Invalid file type. Expected ${acceptMime}/*.` })
        return
      }

      // Validate file size (per-type limit)
      const sizeError = getFileSizeError(file.size, acceptMime)
      if (sizeError) {
        setError(sizeError)
        updateNodeData({ status: 'error', error: sizeError })
        return
      }

      setError(null)
      setIsUploading(true)

      // Retain file for retry on failure
      lastFileRef.current = file

      // Revoke previous object URL
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }

      // Create object URL for immediate preview
      const url = URL.createObjectURL(file)
      objectUrlRef.current = url
      setPreviewUrl(url)

      const outField = MEDIA_TO_OUT_FIELD[acceptMime] || `out_${acceptMime}`
      updateNodeData({
        status: 'running',
        in_file_name: file.name,
        error: undefined,
      })

      // Upload via backend API (write-through to GCS via the fs_storage shim)
      try {
        const result = await api.uploadAsset(projectId, file)

        // Store the relative path in the out_* field (YDoc-persisted).
        // Display URL is derived at render time via api.getAssetUrl().
        // Keep the blob preview URL alive — clearing it now would cause a
        // flash of empty state because out_image propagates through YDoc
        // asynchronously and arrives a frame later. The component clears
        // previewUrl once it sees out_image from YDoc (see useEffect below).
        updateNodeData({
          status: 'completed',
          [outField]: result.path,
          _initialPreviewUrl: undefined,
        })

        lastFileRef.current = null
        setIsUploading(false)
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unable to upload'
        setError(msg)
        updateNodeData({ status: 'error', error: msg })
        setIsUploading(false)

        // Revoke object URL on error
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current)
          objectUrlRef.current = null
          setPreviewUrl(null)
        }
      }
    },
    [acceptMime, updateNodeData, projectId]
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      processFile(file)
    },
    [processFile]
  )

  const dragHandlers = {
    onDragOver: useCallback((e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(true)
    }, []),
    onDragLeave: useCallback((e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)
    }, []),
    onDrop: useCallback(
      (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
        const files = e.dataTransfer.files
        if (files.length > 1) {
          setMultiFileWarning('Only one file per node')
          setTimeout(() => setMultiFileWarning(null), 3000)
        }
        const file = files[0]
        if (file) processFile(file)
      },
      [processFile]
    ),
  }

  const retry = useCallback(() => {
    if (lastFileRef.current) {
      processFile(lastFileRef.current)
    }
  }, [processFile])

  const clear = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }
    lastFileRef.current = null
    setPreviewUrl(null)
    setIsUploading(false)
    setError(null)
    setMultiFileWarning(null)
    const outField = MEDIA_TO_OUT_FIELD[acceptMime] || `out_${acceptMime}`
    updateNodeData({
      status: 'idle',
      [outField]: undefined,
      in_file_name: undefined,
      error: undefined,
    })
  }, [acceptMime, updateNodeData])

  // NOTE: Auto-upload of initialFile is NOT handled here because the
  // updateNodeData callback may capture a stale canvas context on first mount.
  // Each upload node component handles initialFile via its own useEffect that
  // waits for the canvas context to be available before calling uploadFile().

  return {
    previewUrl,
    isUploading,
    error,
    handleFileChange,
    dragHandlers,
    isDragOver,
    retry,
    clear,
    multiFileWarning,
    uploadFile: processFile,
  }
}

/**
 * Format file size for display.
 * Shows KB for < 1MB, MB for >= 1MB.
 */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  return `${(bytes / 1024).toFixed(1)} KB`
}
